import { handlers } from './api/index'

const mn = '[api/msw/browser.ts]'

// How long to wait for the freshly-registered worker to actually CONTROL this
// page before giving up and letting the app boot anyway. Generous: on a cold dev
// server the first load compiles the whole handler tree, and a slow activation
// there is not a failure.
const CONTROL_TIMEOUT_MS = 10_000

function isControlledByMockWorker() {
  const controller = navigator.serviceWorker?.controller
  return !!controller?.scriptURL.includes('mockServiceWorker.js')
}

/**
 * Resolve once THIS page is controlled by the MSW worker.
 *
 * `worker.start()` resolves when the worker is registered and activated, which
 * is not the same thing as the page being controlled by it — a request issued
 * from an uncontrolled client bypasses the service worker entirely and goes to
 * the real API. In practice activation and control land together (measured: the
 * controller arrives ~200ms in, well before the first `/api` call), so this is a
 * guard rather than a fix for an observed escape. It makes the ordering explicit
 * instead of incidental, and turns "mocks silently not intercepting" into a
 * logged error.
 *
 * Never throws: a timeout here means mocks may be inactive, which is worth
 * shouting about but is not worth refusing to boot over.
 */
async function waitForMockWorkerControl(): Promise<boolean> {
  const fn = `${mn} waitForMockWorkerControl |`
  if (!('serviceWorker' in navigator)) {
    return false
  }
  if (isControlledByMockWorker()) {
    return true
  }
  const controlled = await new Promise<boolean>((resolve) => {
    const onControllerChange = () => {
      if (isControlledByMockWorker()) {
        finish(true)
      }
    }
    const timer = setTimeout(() => finish(false), CONTROL_TIMEOUT_MS)
    function finish(result: boolean) {
      clearTimeout(timer)
      navigator.serviceWorker.removeEventListener(
        'controllerchange',
        onControllerChange
      )
      resolve(result)
    }
    navigator.serviceWorker.addEventListener(
      'controllerchange',
      onControllerChange
    )
    // Re-check: control can arrive between the guard above and the listener.
    onControllerChange()
  })
  if (!controlled) {
    console.error(
      `${fn} The mock service worker did not take control within` +
        ` ${CONTROL_TIMEOUT_MS}ms. Requests will reach the REAL API.`
    )
  }
  return controlled
}

export async function startMSWWorker(apiBase: string) {
  console.debug('Importing setupWorker from msw/browser.')
  // msw@2 moved setupWorker from the 'msw' root to 'msw/browser'.
  const { setupWorker } = await import('msw/browser')
  console.log('Configuring mock service worker.')
  const worker = setupWorker(...handlers(apiBase))
  console.log('Starting mock service worker.')
  await worker.start({
    onUnhandledRequest: 'bypass', // disable logging of requests not handled by MSW.
    // v1's `waitUntilReady: true` was the default and the option was removed
    // in v2 (deferring requests until worker activation is now built-in).
  })
  return { controlled: await waitForMockWorkerControl() }
}
